// src/pages/Admin.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatDateTime, roleLabel, getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('accountability');
  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [summary, setSummary] = useState([]);
  const [lateChecks, setLateChecks] = useState([]);
  const [writeups, setWriteups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [writeupModal, setWriteupModal] = useState(null);
  const [writeupNotes, setWriteupNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [newRoleLabel, setNewRoleLabel] = useState('');
  const [newRoleLevel, setNewRoleLevel] = useState(1);
  const [staffForm, setStaffForm] = useState({ firstName: '', lastName: '', email: '', phone: '', role: 'mht', pin: '', receivesAlerts: false, alertPhone: '' });
  const [editForm, setEditForm] = useState({});
  const [staffError, setStaffError] = useState('');
  const [showWriteupHistory, setShowWriteupHistory] = useState(false);

  useEffect(() => { loadData(); }, [fromDate, toDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [staffRes, summaryRes, lateRes, rolesRes] = await Promise.all([
        api.get('/staff'),
        api.get('/accountability/staff-summary', { params: { fromDate, toDate } }),
        api.get('/accountability/late-checks', { params: { fromDate, toDate } }),
        api.get('/staff-roles')
      ]);
      setStaffList(staffRes.data || []);
      setSummary(summaryRes.data || []);
      const allLate = lateRes.data || [];
      setLateChecks(allLate.filter(l => !l.writeup_issued));
      setWriteups(allLate.filter(l => l.writeup_issued));
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    } catch (err) {
      console.error('Admin load error', err);
    } finally {
      setLoading(false);
    }
  };

  const issueWriteup = async () => {
    if (!writeupNotes.trim()) { alert('Please add writeup notes'); return; }
    setSaving(true);
    try {
      await api.patch(`/accountability/${writeupModal.id}/writeup`, { writeupIssued: true, writeupNotes });
      setWriteupModal(null); loadData();
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const addStaff = async (e) => {
    e.preventDefault(); setSaving(true); setStaffError('');
    try {
      await api.post('/staff', staffForm);
      setShowAddStaff(false);
      setStaffForm({ firstName: '', lastName: '', email: '', phone: '', role: roles[0]?.name || 'mht', pin: '', receivesAlerts: false, alertPhone: '' });
      loadData();
    } catch (err) { setStaffError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const saveEditStaff = async (e) => {
    e.preventDefault(); setSaving(true); setStaffError('');
    try {
      await api.patch(`/staff/${editStaff.id}`, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        role: editForm.role,
        receivesAlerts: editForm.receivesAlerts,
        appearsOnSchedule: editForm.appearsOnSchedule,
        alertPhone: editForm.alertPhone,
      });
      setEditStaff(null); loadData();
    } catch (err) { setStaffError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const openEditStaff = (s) => {
    setEditStaff(s);
    setEditForm({
      firstName: s.first_name,
      lastName: s.last_name,
      email: s.email || '',
      phone: s.phone || '',
      role: s.role,
      receivesAlerts: s.receives_alerts || false,
      appearsOnSchedule: s.appears_on_schedule !== false,
      alertPhone: s.alert_phone || '',
    });
    setStaffError('');
  };

  const addRole = async () => {
    if (!newRoleLabel.trim()) return;
    const name = newRoleLabel.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    try {
      await api.post('/staff-roles', { name, label: newRoleLabel.trim(), permissionLevel: parseInt(newRoleLevel) });
      setNewRoleLabel(''); loadData();
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const removeRole = async (id, label, isSystem) => {
    if (isSystem) { alert('System roles cannot be removed.'); return; }
    if (!window.confirm(`Remove "${label}" role?`)) return;
    try { await api.delete(`/staff-roles/${id}`); loadData(); }
    catch (err) { alert(getErrorMessage(err)); }
  };

  const resetPin = async (s) => {
    const newPin = window.prompt(`Reset PIN for ${s.first_name} ${s.last_name}.\nEnter new 4-6 digit PIN:`);
    if (!newPin) return;
    if (!/^\d{4,6}$/.test(newPin)) { alert('PIN must be 4-6 digits'); return; }
    try { await api.post(`/staff/${s.id}/reset-pin`, { newPin }); alert('PIN reset successfully.'); }
    catch (err) { alert(getErrorMessage(err)); }
  };

  const toggleActive = async (s) => {
    if (!window.confirm(`${s.is_active ? 'Deactivate' : 'Reactivate'} ${s.first_name} ${s.last_name}?`)) return;
    try { await api.patch(`/staff/${s.id}`, { isActive: !s.is_active }); loadData(); }
    catch (err) { alert(getErrorMessage(err)); }
  };

  const getRoleLabel = (roleName) => {
    if (!Array.isArray(roles) || !roles.length) return roleLabel(roleName);
    const found = roles.find(r => r.name === roleName);
    return found ? found.label : roleLabel(roleName);
  };

  return (
    <PageWrapper active="admin" title="Admin">

      <div className="tab-row">
        <div className={`tab ${tab === 'accountability' ? 'active' : ''}`} onClick={() => setTab('accountability')}>Accountability</div>
        <div className={`tab ${tab === 'staff' ? 'active' : ''}`} onClick={() => setTab('staff')}>Staff</div>
        <div className={`tab ${tab === 'writeups' ? 'active' : ''}`} onClick={() => setTab('writeups')}>Writeups</div>
      </div>

      <button onClick={() => navigate('/check-options')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 16, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '11px 14px', fontSize: 13, color: 'var(--gray-600)', cursor: 'pointer' }}>
        Manage check options (locations, activities, moods)
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
      </button>

      {/* ── ACCOUNTABILITY ── */}
      {tab === 'accountability' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
          </div>
          {loading ? <div className="spinner" /> : (
            <>
              <div className="section-label">Staff performance</div>
              <div className="card-grid" style={{ marginBottom: 16 }}>
                {summary.map(s => (
                  <div key={s.id} className="card" style={{ marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{s.staff_name}</div>
                        <div style={{ fontSize: 11, color: '#9CA3AF' }}>{getRoleLabel(s.role)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {s.writeups_issued > 0 && <div style={{ fontSize: 11, color: '#A32D2D', fontWeight: 500 }}>{s.writeups_issued} writeup{s.writeups_issued > 1 ? 's' : ''}</div>}
                        <span style={{ fontSize: 12, color: parseInt(s.late_percentage) > 20 ? '#A32D2D' : 'var(--primary)' }}>{s.late_percentage || 0}% late</span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
                      {[['Total', s.total_checks], ['Late', s.late_checks], ['Missed', s.missed_checks]].map(([label, val]) => (
                        <div key={label} style={{ background: '#F9FAFB', borderRadius: 6, padding: 6, textAlign: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 600, color: label === 'Missed' && val > 0 ? '#A32D2D' : 'var(--primary)' }}>{val}</div>
                          <div style={{ fontSize: 10, color: '#9CA3AF' }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="divider" />
              <div className="section-label">Pending writeups</div>
              {lateChecks.length === 0 ? (
                <div className="empty-state" style={{ padding: '1rem' }}>No pending late or missed checks.</div>
              ) : (
                lateChecks.map(lc => (
                  <div key={lc.id} className="card" style={{ borderLeft: `3px solid ${lc.was_missed ? 'var(--red)' : 'var(--amber)'}`, borderRadius: '0 12px 12px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{lc.client_name}</div>
                        <div style={{ fontSize: 12, color: '#9CA3AF' }}>Staff: {lc.staff_name} · Due: {formatDateTime(lc.scheduled_datetime)}</div>
                      </div>
                      <span className="badge" style={{ background: lc.was_missed ? '#FCEBEB' : '#FAEEDA', color: lc.was_missed ? '#A32D2D' : '#854F0B' }}>
                        {lc.was_missed ? 'Missed' : `${lc.minutes_late}m late`}
                      </span>
                    </div>
                    <button className="btn btn-danger btn-sm" style={{ marginTop: 10, width: '100%' }} onClick={() => { setWriteupModal(lc); setWriteupNotes(''); }}>Issue writeup</button>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      {/* ── WRITEUPS ── */}
      {tab === 'writeups' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
          </div>
          {loading ? <div className="spinner" /> : writeups.length === 0 ? (
            <div className="empty-state">No writeups issued in this period.</div>
          ) : (
            writeups.map(w => (
              <div key={w.id} className="card" style={{ borderLeft: '3px solid var(--primary)', borderRadius: '0 12px 12px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{w.staff_name}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>{getRoleLabel(w.role)} · {formatDateTime(w.scheduled_datetime)}</div>
                  </div>
                  <span className="badge" style={{ background: '#FCEBEB', color: '#A32D2D' }}>
                    {w.was_missed ? 'Missed' : `${w.minutes_late}m late`}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#4B5563' }}>Client: {w.client_name}</div>
                {w.writeup_notes && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#FEF0E8', borderRadius: 6, fontSize: 12, color: '#4B5563', borderLeft: '2px solid var(--accent)' }}>
                    {w.writeup_notes}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>Issued on {formatDateTime(w.reviewed_at || w.scheduled_datetime)}</div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── STAFF ── */}
      {tab === 'staff' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setShowAddStaff(true)}>+ Add staff member</button>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowManageRoles(!showManageRoles)}>Manage roles</button>
          </div>

          {showManageRoles && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 12, color: 'var(--primary)' }}>Staff roles</div>
              {roles.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{r.label}</span>
                    <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8 }}>Level {r.permission_level}</span>
                    {r.is_system && <span style={{ fontSize: 10, color: 'var(--primary)', marginLeft: 6, background: 'var(--primary-light)', padding: '1px 6px', borderRadius: 4 }}>system</span>}
                  </div>
                  {!r.is_system && <button onClick={() => removeRole(r.id, r.label, r.is_system)} style={{ fontSize: 12, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>}
                </div>
              ))}
              <div style={{ marginTop: 14 }}>
                <div className="section-label">Add new role</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, marginBottom: 8 }}>
                  <input value={newRoleLabel} onChange={e => setNewRoleLabel(e.target.value)} placeholder="e.g. Counselor, CADC" style={{ flex: 1, padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13 }} />
                  <select value={newRoleLevel} onChange={e => setNewRoleLevel(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13 }}>
                    <option value={1}>Basic staff</option>
                    <option value={2}>Clinical</option>
                    <option value={3}>Supervisor</option>
                  </select>
                </div>
                <button className="btn btn-primary btn-sm" onClick={addRole} disabled={!newRoleLabel.trim()}>Add role</button>
              </div>
            </div>
          )}

          <div className="card-grid">
            {staffList.map(s => (
              <div key={s.id} className="card" style={{ opacity: s.is_active ? 1 : 0.6, marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: 'var(--primary)', flexShrink: 0 }}>
                    {s.first_name[0]}{s.last_name[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{s.first_name} {s.last_name}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                      {getRoleLabel(s.role)}
                      {!s.is_active && <span style={{ color: '#A32D2D', marginLeft: 6 }}>· Inactive</span>}
                    </div>
                    {s.email && <div style={{ fontSize: 12, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.email}</div>}
                    {s.receives_alerts && s.alert_phone && <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 2 }}>✓ SMS alerts</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => openEditStaff(s)}>Edit</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => resetPin(s)}>Reset PIN</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => toggleActive(s)}>{s.is_active ? 'Deactivate' : 'Reactivate'}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Writeup modal */}
      {writeupModal && (
        <div className="modal-overlay" onClick={() => setWriteupModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Issue writeup</h2>
            <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
              {writeupModal.staff_name} · {writeupModal.client_name}<br />
              {writeupModal.was_missed ? 'Missed check' : `${writeupModal.minutes_late} min late`} on {formatDateTime(writeupModal.scheduled_datetime)}
            </p>
            <div className="field">
              <label>Writeup notes</label>
              <textarea value={writeupNotes} onChange={e => setWriteupNotes(e.target.value)} placeholder="Describe the incident, expectations, and any prior warnings..." style={{ minHeight: 90 }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-danger" onClick={issueWriteup} disabled={saving}>{saving ? 'Saving...' : 'Issue writeup'}</button>
              <button className="btn btn-secondary" onClick={() => setWriteupModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add staff modal */}
      {showAddStaff && (
        <div className="modal-overlay" onClick={() => setShowAddStaff(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <h2>Add staff member</h2>
            <form onSubmit={addStaff}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="field"><label>First name</label><input required value={staffForm.firstName} onChange={e => setStaffForm(f => ({ ...f, firstName: e.target.value }))} /></div>
                <div className="field"><label>Last name</label><input required value={staffForm.lastName} onChange={e => setStaffForm(f => ({ ...f, lastName: e.target.value }))} /></div>
              </div>
              <div className="field"><label>Work email <span style={{color:"var(--red)"}}>*</span></label><input type="email" required value={staffForm.email} onChange={e => setStaffForm(f => ({ ...f, email: e.target.value }))} placeholder="staff@numabh.com" /></div>
              <div className="field"><label>Phone (optional)</label><input type="tel" value={staffForm.phone} onChange={e => setStaffForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div className="field">
                <label>Role</label>
                <select value={staffForm.role} onChange={e => setStaffForm(f => ({ ...f, role: e.target.value }))}>
                  {roles.map(r => <option key={r.id} value={r.name}>{r.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Initial PIN (4–6 digits)</label>
                <input type="password" inputMode="numeric" required maxLength={6} value={staffForm.pin} onChange={e => setStaffForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))} placeholder="e.g. 1234" />
              </div>
              <div className="field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={staffForm.receivesAlerts} onChange={e => setStaffForm(f => ({ ...f, receivesAlerts: e.target.checked }))} style={{ width: 'auto' }} />
                  Receives overdue SMS alerts
                </label>
              </div>
              {staffForm.receivesAlerts && (
                <div className="field"><label>Alert phone</label><input type="tel" value={staffForm.alertPhone} onChange={e => setStaffForm(f => ({ ...f, alertPhone: e.target.value }))} placeholder="+1 (310) 555-0000" /></div>
              )}
              {staffError && <div className="alert-bar danger">{staffError}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add staff'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setShowAddStaff(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit staff modal */}
      {editStaff && (
        <div className="modal-overlay" onClick={() => setEditStaff(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <h2>Edit — {editStaff.first_name} {editStaff.last_name}</h2>
            <form onSubmit={saveEditStaff}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="field"><label>First name</label><input required value={editForm.firstName} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} /></div>
                <div className="field"><label>Last name</label><input required value={editForm.lastName} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} /></div>
              </div>
              <div className="field"><label>Work email <span style={{color:"var(--red)"}}>*</span></label><input type="email" required value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} placeholder="staff@numabh.com" /></div>
              <div className="field"><label>Phone</label><input type="tel" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div className="field">
                <label>Role</label>
                <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                  {roles.map(r => <option key={r.id} value={r.name}>{r.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={editForm.receivesAlerts} onChange={e => setEditForm(f => ({ ...f, receivesAlerts: e.target.checked }))} style={{ width: 'auto' }} />
                  Receives overdue SMS alerts
                </label>
              </div>
              {editForm.receivesAlerts && (
                <div className="field"><label>Alert phone</label><input type="tel" value={editForm.alertPhone} onChange={e => setEditForm(f => ({ ...f, alertPhone: e.target.value }))} placeholder="+1 (310) 555-0000" /></div>
              )}
              <div className="field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={editForm.appearsOnSchedule} onChange={e => setEditForm(f => ({ ...f, appearsOnSchedule: e.target.checked }))} style={{ width: 'auto' }} />
                  Appears on the shift schedule
                </label>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Turn on for anyone who covers shifts, including nurses and supervisors filling in. Independent of SMS alerts.
                </div>
              </div>
              {staffError && <div className="alert-bar danger">{staffError}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setEditStaff(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </PageWrapper>
  );
}
